import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CostDataGrid = ({ costs, onCostSelect, selectedCostId, onEdit, onDelete }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedCosts = [...costs]?.sort((a, b) => {
    if (sortConfig?.key === 'date') {
      return sortConfig?.direction === 'asc' 
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date);
    }
    if (sortConfig?.key === 'amount') {
      return sortConfig?.direction === 'asc' 
        ? a?.amount - b?.amount
        : b?.amount - a?.amount;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedCosts?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCosts = sortedCosts?.slice(startIndex, startIndex + itemsPerPage);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock' },
      approved: { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      rejected: { bg: 'bg-error/10', text: 'text-error', icon: 'XCircle' },
      reimbursed: { bg: 'bg-primary/10', text: 'text-primary', icon: 'DollarSign' },
      processing: { bg: 'bg-accent/10', text: 'text-accent', icon: 'RefreshCw' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      inspection: 'ClipboardCheck',
      lab: 'FlaskConical',
      customs: 'FileCheck',
      courier: 'Package',
      travel: 'Plane',
      legal: 'Scale',
      other: 'MoreHorizontal'
    };
    return icons?.[category] || 'MoreHorizontal';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Date
                  <Icon name={sortConfig?.key === 'date' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Vendor</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Description</th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors ml-auto"
                >
                  Amount
                  <Icon name={sortConfig?.key === 'amount' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Currency</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground">Documents</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedCosts?.map((cost) => (
              <tr
                key={cost?.id}
                onClick={() => onCostSelect(cost?.id)}
                className={`hover:bg-muted/30 cursor-pointer transition-colors ${
                  selectedCostId === cost?.id ? 'bg-primary/5' : ''
                }`}
              >
                <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                  {new Date(cost.date)?.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Building2" size={14} className="text-muted-foreground" />
                    {cost?.vendor}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name={getCategoryIcon(cost?.category)} size={14} className="text-muted-foreground" />
                    {cost?.categoryLabel}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                  {cost?.description}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right font-medium">
                  {cost?.amount?.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {cost?.currency}
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(cost?.status)}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{cost?.documents}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEdit(cost?.id);
                      }}
                      className="p-1.5 hover:bg-muted rounded-md transition-colors"
                      aria-label="Edit cost"
                    >
                      <Icon name="Edit2" size={14} className="text-muted-foreground hover:text-foreground" />
                    </button>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onDelete(cost?.id);
                      }}
                      className="p-1.5 hover:bg-error/10 rounded-md transition-colors"
                      aria-label="Delete cost"
                    >
                      <Icon name="Trash2" size={14} className="text-muted-foreground hover:text-error" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedCosts?.length)} of {sortedCosts?.length} costs
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CostDataGrid;